import { useCallback, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import type { CompanyState } from "../../types/company";
import type { Action } from "../../lib/company";
import { CompanyContext } from "../../lib/companyContext";
import { toast } from "../../lib/toast";

interface CompanyProviderProps {
  initialState: CompanyState;
  children: ReactNode;
}

/**
 * Holds the company area's data in memory (reset on page reload).
 * To use the API, have `run` call the endpoint, then refresh `state`.
 */
export default function CompanyProvider({
  initialState,
  children,
}: CompanyProviderProps) {
  const [state, setState] = useState(initialState);
  // Actions read the latest state synchronously, even twice in one handler.
  const stateRef = useRef(state);

  const run = useCallback(
    <A extends unknown[]>(action: Action<A>, ...args: A) => {
      const [next, result] = action(stateRef.current, ...args);
      stateRef.current = next;
      setState(next);
      return result;
    },
    [],
  );

  // Shown by the app-wide <Toaster />.
  const value = useMemo(() => ({ state, run, toast }), [state, run]);

  return (
    <CompanyContext.Provider value={value}>
      {children}
    </CompanyContext.Provider>
  );
}
